import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { FactoryIcon, Plus, PlayCircle, XCircle, CheckCircle2 } from "lucide-react";

export default function ProductionOrders() {
  const qc = useQueryClient();
  const { data: orders = [] } = useQuery({
    queryKey: ["prod-orders"],
    queryFn: async () => (await api.get("/production-orders")).data,
  });
  const [creating, setCreating] = useState(false);
  const [running, setRunning] = useState(null); // order to complete

  return (
    <div className="space-y-6 animate-fade-in" data-testid="production-page">
      <div className="flex justify-between items-start gap-4">
        <div>
          <div className="data-label">Manufacturing</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">Production Orders</h1>
        </div>
        <button onClick={() => setCreating(true)} className="btn-primary" data-testid="po-new">
          <Plus className="w-4 h-4 mr-1" /> New order
        </button>
      </div>

      {creating && <CreateOrder onDone={() => { setCreating(false); qc.invalidateQueries({ queryKey: ["prod-orders"] }); }} />}
      {running && <CompleteRun order={running}
                    onCancel={() => setRunning(null)}
                    onDone={() => { setRunning(null); qc.invalidateQueries(); }} />}

      <div className="bg-white border border-border rounded-sm divide-y divide-border" data-testid="orders-list">
        {orders.length === 0 && <div className="p-6 text-sm text-muted-foreground">No production orders.</div>}
        {orders.map(o => (
          <div key={o.id} className="p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`po-${o.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="container-code text-primary">{o.order_number}</span>
                <span className={`badge-flat ${
                  o.status === "completed" ? "bg-success/10 text-success"
                  : o.status === "cancelled" ? "bg-muted text-muted-foreground"
                  : "bg-secondary/15 text-secondary"}`}>
                  {o.status}
                </span>
              </div>
              <div className="text-sm mt-1">{o.formulation_name} · v{o.formulation_version}</div>
              <div className="text-xs text-muted-foreground">
                Target {o.desired_output_qty} · {o.created_by_name} · {new Date(o.started_at).toLocaleString()}
              </div>
            </div>
            {o.status === "draft" && (
              <div className="flex gap-2">
                <button onClick={() => setRunning(o)} className="btn-primary"
                        data-testid={`po-run-${o.id}`}>
                  <PlayCircle className="w-4 h-4 mr-1" /> Run & complete
                </button>
                <button onClick={() => cancel(o.id, qc)} className="btn-ghost text-xs"
                        data-testid={`po-cancel-${o.id}`}>
                  <XCircle className="w-3 h-3 mr-1" /> Cancel
                </button>
              </div>
            )}
            {o.status === "completed" && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <CheckCircle2 className="w-4 h-4 text-success" />
                Yield {o.output_qty_actual}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

async function cancel(id, qc) {
  if (!window.confirm("Cancel this order?")) return;
  try {
    await api.post(`/production-orders/${id}/cancel`);
    toast.success("Cancelled");
    qc.invalidateQueries();
  } catch (e) { toast.error(formatError(e)); }
}

function CreateOrder({ onDone }) {
  const { data: formulations = [] } = useQuery({
    queryKey: ["formulations-active"],
    queryFn: async () => (await api.get("/formulations")).data.filter(f => f.status === "active"),
  });
  const { data: locations = [] } = useQuery({
    queryKey: ["locations"],
    queryFn: async () => (await api.get("/locations")).data.filter(l => l.active),
  });
  const [fid, setFid] = useState("");
  const [qty, setQty] = useState("");
  const [loc, setLoc] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/production-orders", {
        formulation_id: fid, desired_output_qty: Number(qty),
        output_location_id: loc || null,
      });
      toast.success("Order created");
      onDone();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-primary/40 rounded-sm p-4 space-y-3" data-testid="po-create-form">
      <h2 className="font-display text-xl font-semibold">New production order</h2>
      <div className="grid md:grid-cols-3 gap-3">
        <label>
          <span className="data-label">Formulation</span>
          <select className="field mt-1" value={fid} onChange={(e) => setFid(e.target.value)}
                  data-testid="po-formulation">
            <option value="">Choose…</option>
            {formulations.map(f => <option key={f.id} value={f.id}>{f.name} (v{f.version})</option>)}
          </select>
        </label>
        <label>
          <span className="data-label">Desired output qty</span>
          <input type="number" step="0.001" className="field mt-1 font-mono"
                 value={qty} onChange={(e) => setQty(e.target.value)} data-testid="po-qty" />
        </label>
        <label>
          <span className="data-label">Output location</span>
          <select className="field mt-1" value={loc} onChange={(e) => setLoc(e.target.value)}
                  data-testid="po-location">
            <option value="">Choose…</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.code} · {l.name}</option>)}
          </select>
        </label>
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onDone} className="btn-ghost" data-testid="po-create-cancel">Cancel</button>
        <button onClick={submit} disabled={busy || !fid || !qty} className="btn-primary"
                data-testid="po-create-submit">
          {busy ? "Creating…" : "Create"}
        </button>
      </div>
    </div>
  );
}

function CompleteRun({ order, onCancel, onDone }) {
  const [availability, setAvailability] = useState(null);
  const [allocations, setAllocations] = useState({}); // {item_id: [{container_id, qty}]}
  const [outputLoc, setOutputLoc] = useState(order.output_location_id || "");
  const [outLines, setOutLines] = useState([{ count: 1, qty_each: "", description: "" }]);
  const [actualYield, setActualYield] = useState(String(order.desired_output_qty));
  const [lossNote, setLossNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    api.get(`/production-orders/${order.id}/availability`).then(r => {
      setAvailability(r.data);
      const init = {};
      r.data.lines.forEach(l => { init[l.item_id] = l.proposal.map(p => ({ container_id: p.container_id, qty: p.qty, container_code: p.container_code })); });
      setAllocations(init);
    });
    api.get("/locations").then(r => setLocations(r.data.filter(l => l.active)));
  }, [order.id]);

  const outSum = useMemo(() => outLines.reduce((s, l) => s + (Number(l.count) || 0) * (Number(l.qty_each) || 0), 0), [outLines]);
  const matches = Math.abs(outSum - Number(actualYield || 0)) < 0.0001 && Number(actualYield) > 0;

  const updLine = (i, p) => setOutLines(L => L.map((x, ix) => ix === i ? { ...x, ...p } : x));

  const submit = async () => {
    setBusy(true);
    try {
      const all = Object.values(allocations).flat().map(a => ({ container_id: a.container_id, qty: Number(a.qty) })).filter(a => a.qty > 0);
      await api.post(`/production-orders/${order.id}/complete`, {
        allocations: all, output_location_id: outputLoc,
        output_container_lines: outLines.map(l => ({ count: Number(l.count), qty_each: Number(l.qty_each), description: l.description })),
        actual_yield_qty: Number(actualYield),
        loss_note: lossNote || null,
        packaging: [],
      });
      toast.success("Production complete");
      onDone();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  if (!availability) return <div className="bg-white border border-border rounded-sm p-4 text-sm">Computing availability…</div>;

  return (
    <div className="bg-white border border-primary/40 rounded-sm p-4 space-y-4" data-testid="po-complete-form">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div>
          <h2 className="font-display text-xl font-semibold">Run — {order.order_number}</h2>
          <div className="text-xs text-muted-foreground">Target {order.desired_output_qty}</div>
        </div>
        {!availability.can_start && (
          <span className="badge-flat bg-destructive/10 text-destructive">Short by {availability.total_shortfall}</span>
        )}
      </div>

      <div className="space-y-3">
        <span className="data-label">Ingredient allocation (from proposal — override if needed)</span>
        {availability.lines.map(line => (
          <div key={line.item_id} className="border border-border rounded-sm" data-testid={`po-ing-${line.item_id}`}>
            <div className="px-3 py-2 border-b border-border flex items-center justify-between text-sm">
              <div>
                <span className="badge-flat bg-muted mr-2">{line.sequence}</span>
                <span className="font-semibold">{line.item_name}</span>
                <span className="text-muted-foreground ml-1">({line.percentage}%)</span>
              </div>
              <div className="font-mono text-xs">
                Need <span className="font-semibold">{line.needed_qty}</span>
                {line.shortfall > 0 && <span className="text-destructive ml-2">short {line.shortfall}</span>}
              </div>
            </div>
            <ul className="text-xs divide-y divide-border">
              {(allocations[line.item_id] || []).map((a, i) => (
                <li key={a.container_id} className="p-2 flex gap-2 items-center" data-testid={`po-ing-${line.item_id}-alloc-${i}`}>
                  <span className="container-code text-primary flex-1 truncate">{a.container_code}</span>
                  <input type="number" step="0.001" className="field-sm w-24 font-mono"
                         value={a.qty}
                         onChange={(e) => setAllocations(A => ({
                           ...A,
                           [line.item_id]: A[line.item_id].map((x, ix) => ix === i ? { ...x, qty: e.target.value } : x),
                         }))}
                         data-testid={`po-ing-${line.item_id}-alloc-${i}-qty`} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="border-t border-border pt-4 space-y-3">
        <span className="data-label">Output container breakdown</span>
        <div className="grid md:grid-cols-2 gap-3">
          <label>
            <span className="data-label">Output location</span>
            <select className="field mt-1" value={outputLoc} onChange={(e) => setOutputLoc(e.target.value)}
                    data-testid="po-out-loc">
              <option value="">Choose…</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.code} · {l.name}</option>)}
            </select>
          </label>
          <label>
            <span className="data-label">Actual yield qty</span>
            <input type="number" step="0.001" className="field mt-1 font-mono"
                   value={actualYield} onChange={(e) => setActualYield(e.target.value)}
                   data-testid="po-yield" />
          </label>
        </div>
        <div className="space-y-2">
          {outLines.map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`po-out-line-${i}`}>
              <input type="number" min="1" className="field-sm col-span-2 font-mono text-center"
                     placeholder="Qty" value={l.count}
                     onChange={(e) => updLine(i, { count: e.target.value })}
                     data-testid={`po-out-line-${i}-count`} />
              <span className="text-muted-foreground text-center">×</span>
              <input type="number" step="0.001" className="field-sm col-span-3 font-mono"
                     placeholder="Qty each" value={l.qty_each}
                     onChange={(e) => updLine(i, { qty_each: e.target.value })}
                     data-testid={`po-out-line-${i}-qty`} />
              <input className="field-sm col-span-5" placeholder="Container description"
                     value={l.description}
                     onChange={(e) => updLine(i, { description: e.target.value })}
                     data-testid={`po-out-line-${i}-desc`} />
              <button onClick={() => setOutLines(L => L.filter((_, ix) => ix !== i))}
                      disabled={outLines.length === 1}
                      className="col-span-1 text-muted-foreground hover:text-destructive"
                      data-testid={`po-out-line-${i}-rm`}>×</button>
            </div>
          ))}
          <button onClick={() => setOutLines(L => [...L, { count: 1, qty_each: "", description: "" }])}
                  className="text-primary text-xs font-semibold uppercase tracking-widest"
                  data-testid="po-out-add">+ Add row</button>
        </div>
        <div className={`text-sm ${matches ? "text-success" : "text-muted-foreground"}`}>
          Container sum: <span className="font-mono font-semibold">{outSum.toFixed(3)}</span> / <span className="font-mono">{actualYield}</span>
        </div>
        <input className="field" placeholder="Loss note (optional)"
               value={lossNote} onChange={(e) => setLossNote(e.target.value)}
               data-testid="po-loss-note" />
      </div>

      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="btn-ghost" data-testid="po-run-cancel">Cancel</button>
        <button onClick={submit} disabled={busy || !matches || !outputLoc || !availability.can_start}
                className="btn-primary" data-testid="po-run-submit">
          {busy ? "Completing…" : "Complete production"}
        </button>
      </div>
    </div>
  );
}
